import CssBaseline from '@mui/material/CssBaseline';
import themes from '@mvf/external-components/Themes/themes';
import { keys } from 'lodash';
import { Global } from '@emotion/react';
import { StyledEngineProvider } from '@mui/material';
import Widget from '../../../../src/Components/Widget/Widget';
import FEATURES from '../../../../src/Data/features';
import { buildPossibleFeatureFlags } from '../../../../src/Helpers/buildPossibleFeatureFlags';
import { getChameleonWebformIds } from '../../../../src/Helpers/getChameleonWebformIds';
import makeGlobalStyling from '../../../../styles/makeGlobalSlyles';
import {
  getFormSettingsFromCache,
  getFormSettingsFromCapture,
  setInCache,
} from '../../../../src/Helpers/formSettings';
import getRedisClient from '../../../../src/Services/Redis/getRedisClient';
import { isISRServerPhase } from '../../../../src/Helpers/nextPhase';
import { getBackendPrefix } from '../../../../src/Helpers/getBackendPrefix';

const Home = ({ formSettings }) => (
  /**
   * formSettings is created at build time via the getStaticProps function - i.e. we get the formSettings prior to loading the app, and will need to pass to the slices as the initialState after slicing it up.
   */
  <StyledEngineProvider injectFirst>
    <CssBaseline />
    <Global styles={makeGlobalStyling()} />
    <Widget formSettings={formSettings} />
  </StyledEngineProvider>
);

export async function getStaticPaths() {
  const webformIds = await getChameleonWebformIds();
  const themeNames = keys(themes);
  const possibleFeatureFlags = buildPossibleFeatureFlags(FEATURES);
  const paths = [];
  webformIds.forEach((webformId) => {
    themeNames.forEach((themeName) => {
      possibleFeatureFlags.forEach((featureFlags) => {
        paths.push({
          params: {
            webformId: String(webformId),
            themeName,
            featureFlags,
          },
        });
      });
    });
  });
  return { paths, fallback: 'blocking' };
}

/**
 * getStaticProps is called at build time on server-side
 *
 * It fetches data for chameleon, which is passed to Home
 */
export async function getStaticProps({ params }) {
  let formSettings;
  const { webformId, themeName, featureFlags } = params;
  let redisClient;

  /*
   * As we can change between capture backends per application variant we need
   * to include both the backend source and the form id in the key to prevent
   * mixing up data in the cache
   */
  const backendPrefix = getBackendPrefix();
  const cacheKey = `formSettings_${backendPrefix}_${webformId}`;
  const lockKey = `lock_${cacheKey}`;
  if (
    isISRServerPhase() && // During the build step the redis client is unavailable, we only utilise for revalidation attempts.
    process.env.NEXT_PUBLIC_ENV === 'staging' // Currently we only utilise the redis cache in staging.
  ) {
    try {
      redisClient = await getRedisClient();
      const lockAcquired = await redisClient.set(lockKey, 'lock', {
        NX: true, // Only set the key if it does not already exist
        EX: 10, // Expire the key after 10 seconds to prevent deadlocks
      });
      if (!lockAcquired) {
        // Another request is already fetching or setting the data, wait and retry
        await new Promise((resolve) => {
          setTimeout(resolve, 100);
        });
        return getStaticProps({ params });
      }
    } catch (e) {
      // Redis is not available, continue without caching
      redisClient = null;
    }
  }
  if (redisClient) {
    formSettings = await getFormSettingsFromCache(cacheKey, redisClient);
  }
  if (!formSettings) {
    try {
      formSettings = await getFormSettingsFromCapture(webformId);
    } catch (e) {
      if (redisClient) {
        await redisClient.del(lockKey);
      }
      return {
        notFound: true,
        revalidate: 10, // in seconds - Next will attempt to regenerate the page when a request comes in at most once every 10 seconds
      };
    }
    if (redisClient) {
      await setInCache(cacheKey, formSettings, redisClient);
    }
  }

  if (redisClient) {
    await redisClient.del(lockKey);
  }

  formSettings.themeName = themeName;
  formSettings.featureFlags =
    featureFlags === 'default' ? [] : featureFlags.split('.');

  return {
    props: { formSettings },
    revalidate: 600, // in seconds (10 minute)
  };
}
export default Home;
